import { lazy, Suspense, useState, type ReactNode } from 'react'

import type {
  Batch,
  BatchCommand,
  HistoryEntry,
  Sample,
  TrayLoad,
  TrayUnload,
} from '@ctcells/contract'
import {
  enumLabel,
  maskPii,
  reviewReasonLabel,
  sampleStatusLabel,
  t,
  type MessageKey,
} from '@ctcells/i18n'

import { Badge } from '../../atoms/Badge.tsx'
import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { StateNotice } from '../../atoms/StateNotice.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { ShortId } from '../../atoms/ShortId.tsx'
import { PageHeader } from '../../layout/PageHeader.tsx'
import { startBlockersText } from '../../shell/startBlockers.ts'
import { WaitReasonBadge } from '../../shell/WaitReasonBadge.tsx'
import { canView, gate, type ActionGate, type ScreenAccess } from '../access.ts'
import { DEMO_BATCH_DETAIL } from '../demoData.ts'
import type { BatchListedField } from './batchListedFields.ts'
import { ownerValue, type BatchOwners } from './batchOwnerColumn.tsx'
import { LoadConfirmModal, LoadConfirmWithDefault } from './LoadConfirmModal.tsx'
import type { RecipeDetailDoc } from './NewBatchRecipePopup.tsx'
import type { LoadConfirmReport } from './loadConfirmReport.ts'
import type { TableSort } from '../../atoms/tableSort.ts'
import { sampleStatusRank, trayWellRank } from '../sortRanks.ts'
import { batchDisplayName } from './batchDisplayName.ts'
import { haltLine, shownHaltLine } from './haltLine.ts'
import { BatchTray, type TrayPlanGroup } from './batchPlanParts.tsx'
import {
  planSampleColumns,
  recipePlanColumns,
  shortSampleId,
  type RecipePlanRow,
} from './batchPlanColumns.tsx'
import { HaltFacts } from './HaltFacts.tsx'
import { HaltLineNotice } from './HaltLineNotice.tsx'
import { needsConfirmResume, timedAtomChoices, type ResumeCheck } from './resumeRouteModel.ts'
import { recoverDecisionRefusal } from './recoverDecisionModel.ts'
import { sampleTestInfoRows } from '../samples/sampleIdentityRows.ts'
import { ResumeRouteModal, type ResumeRouteSubmit } from './ResumeRouteModal.tsx'
import { DispositionModal } from './DispositionModal.tsx'
import {
  dispositionCandidates,
  type DispositionChoice,
  type DispositionOutcome,
} from './dispositionModel.ts'
import { TRAY_SLOTS } from '../monitoring/monitoringModel.ts'
import type { SampleGroupShape } from '../recipes/recipeForm.ts'
import { dateTimeText, durationText, historyTargetText, orDash, timeSecText } from '../format.ts'
import type { BatchDetailData } from '../viewTypes.ts'

import {
  commandIdOf,
  detailText,
  humanReason,
  operationText,
  pendingTrays,
  SAMPLE_ID_COMPACT,
  sampleRowsOf,
  withDetailPriority,
  type SampleRow,
} from './batchDetailModel.ts'

import '../screens.css'
import './newBatch.css'

/*
 * 「레시피」 카드 [상세] 팝업 — 누를 때만 불러온다. 본문 부품(`templates/RecipeDocBody` — 장비 그림 · 템플릿 어휘)이 커서
 * 서랍을 열 때마다 같이 싣지 않는다(작업 추가 창과 같은 팝업 · 같은 컴포넌트다).
 */
const RecipeDetailPopup = lazy(() =>
  import('./NewBatchRecipePopup.tsx').then((m) => ({ default: m.RecipeDetailPopup })),
)

/**
 * 작업 상세 — 와이어프레임 `bdTabs()` 1950행 · `bdBody()` 1957행.
 *
 * 🔴 **버튼 활성은 `batch.allowed_commands` 그대로다.** 와이어프레임은 상태로 계산하지만
 *    그건 데모가 상태를 클라이언트에 들고 있어서다(§11-3 #3). 어떤 버튼이 **보이는가**는
 *    상태가 정하고, **눌리는가**는 서버가 정한다. 둘을 섞지 마라.
 *
 * 🔴 복구 3종(재개 · 폐기 · 완료 확정)은 전부 `POST /batches/{id}/recover` 한 경로다.
 *    따라서 활성 근거도 `recover` 하나다 — 화면이 셋을 따로 판정하지 않는다.
 *
 * 🔴 **확약 2종은 `allowed_commands` 축이 아니다.** 그 enum 다섯(`start`·`stop`·`pause`·
 *    `resume`·`recover`)에 확약이 없다 — 근거는 **`confirmed_at === null` 인 원장 행**이고
 *    경로도 `POST /loads|unloads/{id}/confirm` 으로 다르다. 두 축을 섞지 마라.
 *    ⚠ 본문은 `{command_id}` **하나뿐**이다 — 「평형추 신고」·「수거 사유」 칸을 만들지 마라.
 *      보낼 곳이 없다(`docs/backend-requests.csv` BR-06). 평형추를 요구하는 회차는 모듈이
 *      409 로 막고, 그 409 를 **그 자리에** 보인다. 문구를 지어내지 않는다.
 *
 * 🔴 **탭이 없다 — 카드 넷을 세로로 세운다**(eric 2026-09-30 「이거.. 탭 없애고」).
 *    순서: **작업 · 시료 · 명령 · 적재·수거.** 서랍이 세로로 스크롤된다.
 *    - **시료**: 종전 요약의 「시료 배정」 카드와 「시료」 탭을 **한 표로 합쳤다** — 행이 시료이고 열은
 *      시료 번호 · 상태 · 웰(짝 둘)이다(eric 2026-09-30). 같은 시료를 두 번 그리지 않는다(`sampleRowsOf`).
 *    - **적재·수거**: eric 목록에는 없지만 **남겼다** — 콘솔에서 [적재 확약]을 하는 창구가 여기
 *      하나뿐이다(`useConfirmLoad` 호출부가 이 컨테이너 하나). 빼면 실제 운전이 막힌다
 *    - **이벤트 탭은 뺐다**(같은 날 · eric 목록에 없다). 이벤트 조회(`/history?event_type=event`)도
 *      같이 걷었다. 되살리려면 `eventColumns.tsx` 가 열을 쥐고 있다(상시 작업 줄이 그대로 쓴다)
 *    - 종전 7 → 5 탭에서 뺀 둘(환경 · 배치 보고서)의 사유는 `viewTypes.ts` 에 있다.
 *      보고서는 **시료마다** 있다 — 시료 표의 「보고서」 열은 걷었다(eric 2026-09-30). 시료 목록 · 모니터링 서랍에서 연다.
 *
 * 돌아가는 줄은 **출처를 기억한다** — 작업 목록에서 왔으면 「← 작업」, 이력에서 왔으면 「← 이력」
 * (`cur.bfrom` · §14-1). 그 판단은 호출부가 하고 여기는 문구를 받는다.
 */
export interface BatchDetailScreenProps {
  loadDefaultsEnabled?: boolean | undefined
  /**
   * 부가 조회 없는 자리(`loadDefaultsEnabled` 꺼짐 · 데모)에서 적재 건의 모듈 종류 — 확약 창의 신고 칸은 `centrifuge` 에만 선다.
   * 안 주면 종류를 모르는 것이라 칸이 없다. live 는 창이 서버(`GET /modules/{id}/control`)에서 직접 받는다.
   */
  loadModuleType?: ((load: TrayLoad) => string | null | undefined) | undefined
  localizeTrayPoints?: boolean | undefined
  eventLog?: ReactNode
  data?: BatchDetailData
  access?: ScreenAccess
  loading?: boolean
  /**
   * 탭이 비어 있는 **사유**. 🔴 **「없다」와 「못 봤다」를 가르는 것이 이 prop 의 전부다.**
   *
   * 이 화면의 탭 셋은 역할 경계가 서로 다르다 — `/samples` 와 `/history` ×2 는
   * **operator/admin 한정**이고(`samples.py:81 READ_ROLES` · `history.py PRIVILEGED_ROLES`)
   * viewer 는 403 `INSUFFICIENT_ROLE` 이다. 그런데 배치 자체(`GET /batches/{id}`)는 역할
   * 무관이라 **화면은 멀쩡히 선다** — 그래서 사유가 없으면 viewer 에게는 「시료 없음」·
   * 「이벤트 없음」으로 보인다. **없는 것이 아니라 못 보는 것**인데 거짓을 단언하는 셈이다.
   *
   * ⇒ `LogsScreen.notices` · `MonitoringPanel.chambersNotice` 와 **같은 갈래**다.
   *   표 틀은 그대로 서고 `emptyLabel` 자리에 사유가 선다.
   */
  notices?: {
    samples?: string | undefined
    sampleHistory?: string | undefined
    commands?: string | undefined
  }
  /** 「← 작업」 · 「← 이력」 */
  backLabel?: string
  onBack?: () => void
  /**
   * 서랍으로 열렸을 때의 닫기 — 제목 줄 **오른쪽 위 ×**(eric 2026-09-22 「닫기는 오른쪽 위에 X 로 있어야지」).
   * 넘기면 「← …」 돌아가기 줄은 그리지 않는다.
   */
  onClose?: () => void
  /**
   * **이 상세를 연 목록이 이미 보이고 있는 칸**. 「작업」 카드에서 그 줄을 뺀다
   * (eric 2026-09-23 「이건 row 에 보이는 거니 상세에선 다 빼」).
   *
   * 🔴 **연 쪽이 말한다.** 작업 목록과 이력은 열이 달라서(생성·시작 ↔ 기간) 상세가 스스로
   *    「무엇이 겹치는가」를 알 수 없다. 값은 `batchListedFields.ts` 의 상수 두 개다.
   * 🔴 **안 넘기면 한 줄도 안 빠진다** — 전체 페이지(장비 관점 콘솔 · 태블릿)가 그 경우다.
   */
  listedFields?: readonly BatchListedField[]
  /**
   * 작업자 이름표. 🔴 **넘겼을 때만 「작업」 카드에 「작업자」 줄이 선다**(admin 전용 · eric 2026-09-30 ·
   * `batchOwnerColumn.tsx` 머리말). 넘길지는 컨테이너가 정한다 — 화면은 역할을 모른다.
   */
  owners?: BatchOwners | undefined
  /**
   * 복구 판정. ⚠ `discard` · `confirm_completed` 는 **사유가 필수**이고 `resume` 만 없이 선다 —
   * 그래서 사유는 선택 인자이고, 필요한 갈래는 화면이 대화로 먼저 받는다.
   *
   * ⚠ **되돌려주는 약속이 곧 「닫아도 되는가」다.** 약속이 이행되면 대화를 닫고, 깨지면
   *   열어 둔 채 실패 문구를 보인다 — 사용자가 적은 사유를 잃지 않는다(아래 `submit`).
   *
   * 🔴 **일시정지 · 재개 · 중지는 여기 없다**(eric 2026-09-30 「작업 이름 밑에.. 모니터링에서 보기 · 일시정지 ·
   *    재개 · 중지 버튼... 이거 다 없애줘」). 그 창구는 **상시 작업 줄**(`shell/BatchStrip`) 하나다.
   *    `onCommand` · `onStop` · `onOpenMonitoring` 을 되살리지 마라.
   */
  onRecover?: (
    decision: 'resume' | 'discard' | 'confirm_completed' | 'rerun_cut_atom',
    reason?: string,
    /** 장비를 다시 움직이는 두 판정(`resume` · `rerun_cut_atom`)의 시간 원자 방식 · 현장 확인(`ResumeRouteModal`) */
    opts?: ResumeRouteSubmit,
  ) => void | Promise<unknown>
  /** 멈춘 모듈(`halt_module_id`) → 그 모듈 상세. 셸이 길을 줄 때만 링크다(`HaltFacts`) */
  onOpenModule?: ((moduleId: string) => void) | undefined
  /**
   * **복구 › 시료 처분**(`review_reason=sample_disposition` · `DispositionModal`) — 결정마다 요청을 보내고 결과를 돌려준다.
   * 없으면(목 데이터) 그 단추가 비활성 + 사유다.
   */
  onDispose?:
    | ((
        requests: Record<DispositionChoice, string[]>,
        reason: string,
      ) => Promise<DispositionOutcome>)
    | undefined
  /** 처음부터 처분 창을 연다 — 작업 띠 안내 「이력에서 처리 →」로 들어온 때(`?disposition=open`) · 처분 대기일 때만 */
  initialDisposition?: boolean | undefined
  /** 상태변경 실패 문구. 🔴 화면이 사라지지 않고 머리줄 아래에 남는다 */
  commandError?: string | undefined
  commandPending?: boolean | undefined
  additionalResumeChecks?: readonly ResumeCheck[] | undefined

  /**
   * 적재 확약 — 창(`LoadConfirmModal`)에서 받은 **신고 두 칸**(선택)을 같이 넘긴다. 비운 칸은 키가 없다.
   * 🔴 돌려준 약속이 이행돼야 창이 닫힌다(`mutateAsync`). `void` 면 보낸 즉시 닫는다.
   * 본문의 `command_id` · `confirm_source` 는 컨테이너·훅이 짓는다.
   */
  onConfirmLoad?: (loadId: string, report: LoadConfirmReport) => void | Promise<unknown>
  /** 🔴 수거 확약은 입력을 받지 않는다 — 신고 칸이 계약에 없다(`UnloadConfirmEnvelope`) */
  onConfirmUnload?: (unloadId: string) => void
  /** 요청 중 — 중복 클릭을 막는다(멱등키가 매번 새로 난다) */
  confirming?: boolean
  confirmError?: string | undefined
  /**
   * 확약을 보낼 수 없는 사유(예: 셸이 표면을 선언하지 않아 확약 창구가 없다).
   * 주면 핸들러가 없을 때의 기본 사유(「데모 자료」) 대신 이것이 선다.
   */
  confirmUnavailableReason?: string | undefined
  /**
   * 시작이 막힌 사유(`shell/startBlockers.ts` — 작업 줄과 같은 함수). 비어 있지 않으면
   * 상태 배지에 툴팁 + 스크린리더 문장으로 붙는다. ⚠ 이 화면에는 [시작] 단추가 없다 —
   * 시작 창구는 상시 작업 줄 하나다.
   */
  startBlockers?: readonly string[] | undefined
  /**
   * 머리 오른쪽(× 왼쪽)에 서는 조회 단추 — 지금은 「이벤트 로그 내려받기」 하나다(eric 2026-10-02 · `EventLogDownload`).
   * 🔴 **컨테이너가 만들어 넘긴다** — 무엇을 어디서 받는지는 컨테이너가 안다. 화면은 자리만 준다.
   *    상태변경 단추가 아니라 머리에 서도 「제목 아래 단추 줄」(2026-09-30 걷음)이 되살아나는 것이 아니다.
   */
  headerActions?: ReactNode
  /**
   * 「레시피」 카드 [상세] 팝업이 읽는 그 레시피의 문서 · 요약(작업 추가 창과 같은 팝업 · `NewBatchRecipePopup`).
   * 🔴 작업이 고정한 판의 본문이어야 한다 — 컨테이너가 판이 다르면 문서 대신 사유(`notice`)를 싣는다
   */
  recipeDetail?: RecipeDetailDoc | undefined
  /** [상세] 팝업이 열리고 닫힐 때 — 컨테이너가 열렸을 때만 요약을 묻는다(열지 않으면 요청이 없다) */
  onRecipeDetail?: ((open: boolean) => void) | undefined
  /**
   * 비상정지 중인가(`GET /safety` · `emergency_stop`). 켜져 있으면 「복구 › 재개」가 비활성 + 「비상정지 중」이다 —
   * 서버 발행 게이트가 거절한다(eric 2026-10-14 「E-STOP 상태에서 활성화되면 안 되는 버튼은 비활성화」). 폐기 · 완료 확정은 막지 않는다.
   */
  estopped?: boolean | undefined
  /**
   * **대기 작업 취소**(eric 2026-10-29 「대기 중인 거 버튼이 있었으면」) — `POST /batches/{id}/stop`. 사유는 계약 필수다.
   * 🔴 단추가 **보이는가**는 상태(`created` · `queued`)가 정하고, **눌리는가**는 서버 `allowed_commands` 의 `stop` 이 정한다.
   *    실행 중 작업의 중지는 상시 작업 줄 [중지] 하나다 — 여기 단추는 시작 전 작업에만 선다.
   * 돌려준 약속이 「대화를 닫아도 되는가」다(복구 판정과 같은 규칙). 없으면(목 데이터) 비활성 + 사유.
   */
  onCancel?: ((reason: string) => void | Promise<unknown>) | undefined
}

export function BatchDetailScreen({
  data = DEMO_BATCH_DETAIL,
  access = {},
  loading = false,
  notices,
  backLabel,
  onBack,
  onClose,
  listedFields,
  owners,
  onRecover,
  commandError,
  onConfirmLoad,
  onConfirmUnload,
  confirming = false,
  confirmError,
  confirmUnavailableReason,
  startBlockers = [],
  headerActions,
  eventLog,
  loadDefaultsEnabled = false,
  loadModuleType,
  localizeTrayPoints = false,
  recipeDetail,
  onRecipeDetail,
  estopped = false,
  commandPending = false,
  additionalResumeChecks = [],
  onOpenModule,
  onDispose,
  initialDisposition = false,
  onCancel,
}: BatchDetailScreenProps) {
  /** 사유를 받아야 하는 명령. `null` 이면 대화가 닫혀 있다 */
  const [confirm, setConfirm] = useState<ReasonPrompt | null>(null)
  const [reason, setReason] = useState('')
  const { batch } = data
  const displayName = batchDisplayName(batch)
  const view = canView(access)
  /* 멈춤 한 줄 — 붙었던 구독이 끊겼으면 «지금»을 모르니 안 그린다(마지막 값을 현재로 보이지 않는다) */
  const halt = access.offline === true ? undefined : shownHaltLine(haltLine(batch))
  /*
   * 🔴 **확인 재개 · 시간 원자 방식**(백엔드 0.28.15 · `resumeRoute.tsx`). `resume_route=confirm` 이면 「복구 › 재개」를 서버가 받지 않는다
   *    (`cut_confirm_required`) — 비활성 + 사유로 두고 「복구 › 멈춘 동작부터 다시」를 세운다. 고를 시간 원자 방식이 둘이면 재개도 대화를 거친다.
   */
  const confirmRoute = needsConfirmResume(batch)
  const timedChoices = timedAtomChoices(batch)
  const [resumeModal, setResumeModal] = useState<'resume' | 'rerun_cut_atom' | null>(null)
  /*
   * 🔴 **시료 처분 대기**(`review_reason=sample_disposition` · 중단 뒤 종착 아닌 시료가 남았다 · 03 SVC-SCH-11 ④) — 복구 줄은 [시료 처분]
   *    하나다(재개 · 폐기 · 완료 확정은 중단된 작업에 서지 않는다 · 코어 관리자 페이지 `recoverForm` 과 같은 갈래).
   *    활성은 서버 `allowed_commands`(`recover` — 락 쥔 쓰기 역할에게만 실린다) 그대로다.
   */
  const disposing = batch.review_reason === 'sample_disposition'
  const [dispositionOpen, setDispositionOpen] = useState(initialDisposition && disposing)
  const submitResume = (mode: 'resume' | 'rerun_cut_atom', opts: ResumeRouteSubmit) => {
    if (movingGate(mode).disabled) return
    const sent = onRecover?.(mode, opts.reason, opts)
    void Promise.resolve(sent).then(
      () => setResumeModal(null),
      () => undefined,
    )
  }

  /** 🔴 서버가 준 집합에만 물어본다. `status` 로 만들어 내지 않는다 */
  const openPrompt = (next: ReasonPrompt) => {
    setConfirm(next)
    setReason('')
  }
  const closePrompt = () => {
    setConfirm(null)
    setReason('')
  }

  /**
   * 확인 대화의 「보내기」.
   *
   * 🔴 **성공해야 닫는다.** 종전에는 보내고 나서 아무도 안 닫아서 **성공해도 대화가 그대로
   *    남았다** — 사용자가 같은 버튼을 또 누르게 된다(폐기 · 완료 확정은 되돌릴 수 없는 명령이다).
   *    상시 작업 줄(`shell/StripCommandDialog` 를 쓰는 `BatchStripContainer`)은 처음부터
   *    `mutate(vars, { onSuccess: 닫기 })` 로 닫고 있었다 — 두 대화의 규칙을 맞춘다.
   * 🔴 **실패하면 열어 둔다.** 적은 사유를 잃지 않는다(`commandError` 가 대화 안에 뜬다).
   * ⚠ 콜백이 없으면(목 데이터) 보낼 곳이 없으니 그냥 닫는다.
   */
  const submit = (prompt: ReasonPrompt) => {
    const sent =
      prompt.kind === 'cancel'
        ? onCancel?.(reason.trim())
        : onRecover?.(prompt.decision, reason.trim())
    void Promise.resolve(sent).then(closePrompt, () => undefined)
  }

  const commandGate = (command: BatchCommand): ActionGate => {
    const commandAccess = gate(access, {
      perm: 'control',
      needsLock: true,
      stateOk: batch.allowed_commands.includes(command),
      stateReason: 'reason.commandNotAllowed',
    })
    return commandAccess.disabled
      ? commandAccess
      : commandPending
        ? { disabled: true, disabledReason: t('reason.actionInFlight') }
        : commandAccess
  }
  /**
   * 대기 작업 취소 — 🔴 **보이는가**는 시작 전 상태(`created` · `queued`)가, **눌리는가**는 `allowed_commands` 의 `stop` 이 정한다.
   * 락 · 권한 · 끊김 사유는 다른 명령과 같은 `gate()` 가 낸다.
   */
  const cancelVisible = batch.status === 'created' || batch.status === 'queued'
  const cancelGate = (): ActionGate => {
    const commandAccess = commandGate('stop')
    if (commandAccess.disabled) return commandAccess
    if (onCancel === undefined) return { disabled: true, disabledReason: t('reason.demoSource') }
    return commandAccess
  }
  const disposeGate = (): ActionGate => {
    const commandAccess = commandGate('recover')
    if (commandAccess.disabled) return commandAccess
    if (onDispose === undefined) return { disabled: true, disabledReason: t('reason.demoSource') }
    return commandAccess
  }
  /**
   * 🔴 **복구 › 재개 · 폐기 · 완료 확정(확인 재개 포함)** — `recover` 가 실려 와도 서버가 그 판정을 받는 상태인지 한 번 더 본다
   *    (`recoverDecisionRefusal` · 계약 「aborted 는 discard_samples · rerun_samples 만」). 끝난 작업에 처분할 시료가 남으면 서버가
   *    `recover` 를 싣는데(종착 처분용), 그걸로 세 판정을 켜면 눌러도 409 였다(이송 확인 대기로 끝난 중단 작업 · 2026-10-24).
   *    끊김(stale)은 «지금»을 모르니 그 사유가 먼저다. 그다음은 이 사유 — 락을 쥐어도 안 받으니 먼저 풀 조건이다.
   */
  const decisionGate = (): ActionGate => {
    const commandAccess = commandGate('recover')
    if (access.controlDisabled === true) return commandAccess
    const refused = recoverDecisionRefusal(batch)
    return refused === undefined ? commandAccess : { disabled: true, disabledReason: refused }
  }
  const movingGate = (mode: 'resume' | 'rerun_cut_atom'): ActionGate => {
    const commandAccess = decisionGate()
    if (commandAccess.disabled) return commandAccess
    if (estopped) return { disabled: true, disabledReason: t('reason.estopActive') }
    if (mode === 'resume' && confirmRoute)
      return { disabled: true, disabledReason: t('reason.resumeNeedsConfirm') }
    if (mode === 'rerun_cut_atom' && !confirmRoute)
      return { disabled: true, disabledReason: t('reason.commandNotAllowed') }
    return commandAccess
  }

  return (
    /*
     * 🔴 상세는 **카드 위의 카드**가 아니다(2026-09-22 · 방안 A). `.apageframe` 은 목록용
     *    흰 면이라, 그 안에 다시 카드를 놓으면 흰 면 위 흰 면이 되어 묶음이 안 읽힌다.
     *    상세는 레시피·시료·랩웨어와 같은 `.detailframe`(바탕 위 카드 여러 장)을 쓴다.
     * ⚠ `:root`(다른 앱 4개)에서 둘은 **픽셀이 같다** — 면 색이 바탕과 같고 반경 0 · 그림자 none 이다.
     */
    <div className="detailframe">
      {/*
        🔴 **제목 아래 단추 줄이 없다**(eric 2026-09-30 「작업 진행 중 탭에서.. 작업 이름 밑에.. 모니터링에서 보기 ·
           일시정지 · 재개 · 중지 버튼... 이거 다 없애줘」). 명령 창구는 **상시 작업 줄** 하나다(일시정지 · 재개 ·
           중지 · 복구 · E-STOP). 머리에 남는 것은 오른쪽 위 × 닫기뿐이다.
           ⚠ 작업 줄이 다루는 작업은 «지금 도는 한 건»(`pickStripBatch`)이다 — 여기서 다른 작업을 열어도 그 작업에
             일시정지 · 중지를 내릴 창구는 없다. 복구 대기(`review_reason`)의 판정 셋은 아래 복구 줄에 그대로 남는다.
        ⚠ 머리 「보고서」 단추도 앞서 걷었다(eric 2026-09-30 「보고서 버튼은 빼줘」) — 보고서는 시료마다 있다.
      */}
      <PageHeader
        actions={
          cancelVisible ? (
            <>
              {/* 대기 작업 취소 — 시작 전 작업에만 선다. 목록 행에는 두지 않는다(가끔 쓰는 동작) */}
              <Button
                onClick={() => openPrompt({ kind: 'cancel' })}
                size="pill"
                variant="danger"
                {...cancelGate()}
              >
                {t('batch.cancel.action')}
              </Button>
              {headerActions}
            </>
          ) : (
            headerActions
          )
        }
        onClose={onClose}
        back={
          <button
            aria-label={`${t('action.back')} ${backLabel ?? t('screen.batches.title')}`}
            className="back lnk"
            onClick={onBack}
            type="button"
          >
            <span aria-hidden="true">←</span> {backLabel ?? t('screen.batches.title')}
          </button>
        }
        badge={
          <>
            <WaitReasonBadge
              label={enumLabel('enum.batchStatus', batch.status)}
              reason={startBlockersText(startBlockers)}
              size="pill"
              status={batch.status}
            />
            {batch.review_reason === null ? null : (
              <Badge variant="inconclusive">{reviewReasonLabel(batch.review_reason)}</Badge>
            )}
          </>
        }
        /*
         * 🔴 **제목은 작업 이름뿐이다**(eric 2026-09-30 「작업 이름 있으면.. 작업 이름만.. 작업 이름이 없으면.
         *    작업 id 만」). `batchDisplayName()` 한 곳이 정한다 — 테스트 작업이면 「테스트 작업」, 아니면 서버
         *    `Batch.name`, 없으면 `batch_id`(36자 그대로 · 줄이지 않는다).
         *    종전의 레시피 이름 제목(2026-09-22)과 그 아래 두 줄(작업 ID · 버전 / 배치 이름)은 걷었다 —
         *    레시피는 「작업」 카드의 「레시피」 줄로 내려갔다.
         * ⚠ 제목이 이름이면 **작업 ID 는 제목의 툴팁**(`title`)으로만 싣는다 — 보이는 글자로 두면 「이름만」이 아니다.
         *   식별 · 링크 · 키는 계속 `batch_id` 다.
         */
        title={displayName}
        titleHint={displayName === batch.batch_id ? undefined : batch.batch_id}
      />

      {/*
        🔴 **왜 멈췄는가.** 계약에 없는 칸인데 서버가 더 싣는다(백엔드 `AH-17`) — 2026-09-20
           까지 화면이 받고도 버렸다. 상태만 보이고 까닭은 로그를 뒤져야 알 수 있었다.
        ⚠ `role="status"` 다. 경고가 아니라 **서버가 알려주는 사실**이라 `alert` 로 올리지 않는다.
      */}
      {batch.halt_reason == null || batch.halt_reason === '' ? null : (
        <p className="notice" role="status">
          {batch.halt_reason}
        </p>
      )}

      {/*
        🔴 **멈춤 한 줄**(정본 07 §4.12 · v0.9.5 `resume_headline` · 객체 `resume_blockers`) — 멈춘 것인지 문제인지를 가른다
           (eric 「표시가 없으니까 멈춘건지 문제인건지 알 수가 없다」). 판정은 서버 칸(`haltLine`) · 문장은 서버 것 그대로.
        ⚠ 버튼을 켜고 끄지 않는다 — 활성은 `allowed_commands` 하나다.
      */}
      {halt === undefined ? null : <HaltLineNotice line={halt} />}

      {/* 멈춤 정보 — 서버 값 그대로(시간 · 까닭 모듈 · 원격 호출 예정). 화면이 세지 않는다(`HaltFacts`) */}
      {access.offline === true ? null : <HaltFacts batch={batch} onOpenModule={onOpenModule} />}

      {/* 🔴 상태변경 실패는 **그 자리에** 남는다. 화면을 갈아엎지 않는다 */}
      {commandError === undefined ? null : (
        <p className="deny" role="alert">
          {commandError}
        </p>
      )}

      {/* 복구 판정 3종 — 전부 `POST /batches/{id}/recover`. 활성 근거도 `recover` 하나다 */}
      {disposing ? (
        <div className="btnrow" style={{ paddingTop: 'var(--space-md)' }}>
          <Button
            onClick={() => setDispositionOpen(true)}
            size="pill"
            variant="primary"
            {...disposeGate()}
          >
            {`${t('action.recover')} › ${t('disposition.title')}`}
          </Button>
        </div>
      ) : batch.review_reason === null && !confirmRoute ? null : (
        <div className="btnrow" style={{ paddingTop: 'var(--space-md)' }}>
          {/*
            🔴 **`resume` 만 사유 없이 바로 나간다** — 대화를 거치지 않는다(계약이 그 갈래에만
               `reason` 을 요구하지 않는다).
            ⚠ **블록으로 감싼다.** `onRecover` 가 약속을 돌려줄 수 있어(대화가 그 이행으로 닫는다)
              식으로 두면 `onClick` 이 약속을 받는다 — 여기서는 닫을 대화가 없으니 버린다.
          */}
          {/*
            🔴 **머리말에 「복구」를 붙인다 — 위쪽 [재개]와 «다른 명령»이기 때문이다.**
               위는 `POST /batches/{id}/resume`, 여기는 `recover {decision:"resume"}` 이고,
               **격리 락을 놓는 것은 여기뿐이다**(`services/batches.py` `recover_resume`).
               2026-09-21 까지 둘 다 그냥 「재개」였고, 실물에서 위엣것을 눌러 배치가 되돌아왔다
               (BR-50). 같은 말이 두 개 뜨면 사용자가 고를 수 없다.
            ⚠ 낱말을 새로 만들지 않고 있는 둘을 잇는다 — 사전에 없는 말을 화면이 발명하지 않는다.
          */}
          <Button
            onClick={() => {
              // 고를 시간 원자 방식이 둘이면 대화로 묻는다 — 아니면 종전대로 바로 나간다
              if (timedChoices.length >= 2) setResumeModal('resume')
              else submitResume('resume', {})
            }}
            size="pill"
            {...movingGate('resume')}
          >
            {`${t('action.recover')} › ${t('action.resume')}`}
          </Button>
          {/* 확인 재개 — `resume_route=confirm` 일 때만 선다(그 밖에는 서버가 이 길을 요구하지 않는다) */}
          {confirmRoute ? (
            <Button
              onClick={() => setResumeModal('rerun_cut_atom')}
              size="pill"
              variant="primary"
              {...movingGate('rerun_cut_atom')}
            >
              {`${t('action.recover')} › ${t('action.rerunCutAtom')}`}
            </Button>
          ) : null}
          <Button
            onClick={() => openPrompt({ kind: 'recover', decision: 'discard' })}
            size="pill"
            {...decisionGate()}
          >
            {t('action.discard')}
          </Button>
          <Button
            onClick={() => openPrompt({ kind: 'recover', decision: 'confirm_completed' })}
            size="pill"
            {...decisionGate()}
          >
            {t('action.confirmCompleted')}
          </Button>
          {/*
            ⚠ 「걸린 것 보기」 길은 걷었다 — 가던 곳(관리 › 설정 › 걸린 것)이 2026-10-14 에 없어졌다(eric 「여기 다 안 쓸거야」).
          */}
        </div>
      )}

      {/*
        🔴 **탭이 없다 — 카드 넷이 세로로 선다**(eric 2026-09-30). 스크롤은 `.apage` 가 한다.
        ⚠ 건수는 탭 배지 대신 **표 아래 「N건」**(`DataTable` managed 기본)이 말한다.
      */}
      <div className="apage">
        <div className="cards">
          <BatchCard batch={batch} listedFields={listedFields} owners={owners} />
          <RecipeCard
            createdAt={data.recipeCreatedAt}
            detail={recipeDetail}
            name={data.recipeName}
            onDetail={onRecipeDetail}
            version={batch.recipe_version}
          />
          <SamplesCard
            assignments={data.sampleHistory?.assignments ?? batch.sample_assignments}
            historicalIds={data.sampleHistory?.sampleIds}
            historyIncomplete={data.sampleHistory?.incomplete}
            historyNotice={notices?.sampleHistory}
            groups={data.sampleGroups}
            loading={loading}
            notice={notices?.samples}
            samples={data.samples}
            view={view}
          />
          <CommandsCard
            loading={loading}
            notice={notices?.commands}
            owners={owners}
            rows={data.commands}
          />
          {eventLog}
          <TraysCard
            loadDefaultsEnabled={loadDefaultsEnabled}
            loadModuleType={loadModuleType}
            localizeTrayPoints={localizeTrayPoints}
            access={access}
            confirming={confirming}
            load={data.load}
            loading={loading}
            unload={data.unload}
            {...(confirmError === undefined ? {} : { confirmError })}
            {...(confirmUnavailableReason === undefined ? {} : { confirmUnavailableReason })}
            {...(onConfirmLoad === undefined ? {} : { onConfirmLoad })}
            {...(onConfirmUnload === undefined ? {} : { onConfirmUnload })}
          />
        </div>
      </div>

      {resumeModal === null ? null : (
        <ResumeRouteModal
          key={JSON.stringify([
            batch.batch_id,
            resumeModal,
            batch.version,
            batch.resume_checks,
            timedChoices,
          ])}
          batchId={batch.batch_id}
          checks={[...new Set([...(batch.resume_checks ?? []), ...additionalResumeChecks])]}
          error={commandError}
          mode={resumeModal}
          onClose={() => setResumeModal(null)}
          onSubmit={(opts) => submitResume(resumeModal, opts)}
          timedAtomChoices={timedChoices}
          {...movingGate(resumeModal)}
        />
      )}

      {dispositionOpen && disposing ? (
        <DispositionModal
          batchId={batch.batch_id}
          candidates={dispositionCandidates(
            data.samples.filter((sample) => sample.batch_id === batch.batch_id),
          )}
          notice={notices?.samples}
          onClose={() => setDispositionOpen(false)}
          onSubmit={(requests, reason) =>
            onDispose === undefined
              ? Promise.resolve({ done: [], refused: [], errors: [] })
              : onDispose(requests, reason)
          }
          {...disposeGate()}
        />
      ) : null}

      {confirm === null ? null : (
        <ReasonModal
          batchId={batch.batch_id}
          error={commandError}
          onClose={closePrompt}
          onReason={setReason}
          onSubmit={() => {
            submit(confirm)
          }}
          prompt={confirm}
          reason={reason}
          {...(confirm.kind === 'cancel' ? cancelGate() : decisionGate())}
        />
      )}
    </div>
  )
}

/**
 * 작업 — 🔴 **`Batch` 응답에 있는 칸만 그린다.** (종전 요약 탭의 「작업」 카드 그대로 · 2026-09-30)
 *
 * 뺀 것 둘(2026-09-20):
 *   - **진행률 · 현재 모듈**: REST 에 진행 축이 한 칸도 없다(BR-09). 구독에만 있다
 *   - **알람 카드**: `Alarm` 에 `batch_id` 가 없고 `/alarms` 에 배치 필터도 없다 —
 *     그 배치의 알람을 고를 축이 없었다.
 * 🔴 **「알람 →」 링크도 걷었다**(eric 2026-10-14 「상세보기에서 알람 → 이 문구 제거해줘」). 가던 곳이 이 작업의 알람이
 *   아니라 전체 알람 목록이었다 — 상단 메뉴 「알람」과 같은 곳이다.
 */
function BatchCard({
  batch,
  listedFields,
  owners,
}: {
  batch: Batch
  listedFields?: readonly BatchListedField[] | undefined
  owners?: BatchOwners | undefined
}) {
  /*
   * 🔴 **연 목록이 이미 보이는 줄은 빼고 그린다**(eric 2026-09-23). 값을 «만들지 않는» 것이
   *    아니라 «두 번 읽게 하지 않는» 것이다 — 목록이 없는 자리(전체 페이지)에서는 다 선다.
   */
  const listed = new Set<BatchListedField>(listedFields ?? [])
  const hidden = (field: BatchListedField): boolean => listed.has(field)

  return (
    <div className="card">
      <div className="card__title">{t('column.batch')}</div>
      {/*
        🔴 **두 열 격자**(eric 2026-09-30 「여러 줄인데.. 이걸 2개의 열로 표현해줘」). 쌍이 두 열로 흐른다.
        🔴 **남는 줄은 작업자 · 생성 · 작업 시작 · 작업 종료 · 소요 시간뿐이다**(eric 2026-10-22 「시료 수 빼고... 시작을.. 작업 시작,
           종료를 작업 종료 이렇게 하고 완료 근거 빼고 복구 판정 빼고」). 시료 수는 아래 「시료」 카드의 표 건수가 말한다 ·
           레시피는 「레시피」 카드다. 뺀 줄을 되살리지 마라
      */}
      <ValueRows
        layout="grid"
        rows={[
          /*
           * 🔴 **작업자 — admin 에게만 선다**(eric 2026-09-30 「그거 admin 만 작업자 보이게 해줘」). `owners` 를 받았을
           *    때만이고 값은 목록 열과 같은 함수(`ownerValue`)다 — `GET /users` 의 이름 · 없으면 id. `Batch.name` 을 파싱하지 않는다.
           *    operator · viewer 는 줄 자체가 없다. 목록에서 이 열이 숨으면(서랍 열림) 값이 갈 곳이 이 줄이다.
           */
          ...(owners === undefined || hidden('owner')
            ? []
            : [
                {
                  key: 'owner',
                  label: t('column.owner'),
                  value: ownerValue(owners, batch.created_by ?? null),
                },
              ]),
          ...(hidden('created')
            ? []
            : [
                {
                  key: 'created',
                  label: t('column.createdAt'),
                  value: dateTimeText(batch.created_at),
                },
              ]),
          /* 「작업 시작」 · 「작업 종료」 — 이 카드 전용 이름(목록 머리글 「시작」 · 「종료」와 값은 같다 · eric 2026-10-22) */
          ...(hidden('started')
            ? []
            : [
                {
                  key: 'started',
                  label: t('row.batchStartedAt'),
                  value: dateTimeText(batch.started_at),
                },
              ]),
          /*
           * 🔴 `completed_at` 은 중지(`aborted`)에도 채워진다 — 「완료」라 적지 않는다(`batchEndedColumn` 머리말 · 2026-09-30).
           */
          {
            key: 'completed',
            label: t('row.batchEndedAt'),
            value: dateTimeText(batch.completed_at),
          },
          /* 🔴 응답 두 칸에서 결정된다 — 진행 중이면 `—` 다(화면이 시계를 돌리지 않는다) */
          ...(hidden('duration')
            ? []
            : [
                {
                  key: 'duration',
                  label: t('column.duration'),
                  value: durationText(batch.started_at, batch.completed_at),
                },
              ]),
        ]}
      />
    </div>
  )
}

/**
 * 레시피 — 작업 추가 창의 레시피 표와 **같은 열**(이름 · 버전 · 생성 · `recipePlanColumns`)로 그 작업의 레시피 한 줄을 보인다
 * (eric 2026-10-22 「작업 선택했을 때 상세 내용이... 작업 추가의 내용이 보이도록 해줘」). 읽기 전용 — 라디오는 없고 줄 끝 [상세]가
 * 작업 추가 창과 같은 레시피 상세 팝업(`RecipeDetailPopup` · 바닥 줄 없음 · × · Esc 닫기)을 연다.
 *
 * - 이름 — `Batch.recipe_name`(없으면 `recipe_id` · 컨테이너가 정한다)
 * - 버전 — `Batch.recipe_version`. 🔴 **작업이 만들어질 때 고정된 판**이다 — 레시피의 지금 판이 아니다
 * - 생성 — `GET /recipes/{id}` 의 `created_at`. 🔴 그 응답의 판이 작업의 판과 같을 때만 싣는다(컨테이너) — 다르면 `—`
 */
function RecipeCard({
  name,
  version,
  createdAt,
  detail,
  onDetail,
}: {
  name: string
  version: number
  createdAt?: string | null | undefined
  detail?: RecipeDetailDoc | undefined
  onDetail?: ((open: boolean) => void) | undefined
}) {
  const [open, setOpen] = useState(false)
  const toggle = (next: boolean) => {
    setOpen(next)
    onDetail?.(next)
  }
  const row: RecipePlanRow = {
    name,
    version,
    created_text: createdAt == null ? t('mask.none') : dateTimeText(createdAt),
  }
  return (
    <div className="card">
      <div className="card__title">{t('column.recipe')}</div>
      <DataTable
        caption={t('column.recipe')}
        /* 좁으면 「생성」이 빠지고 행을 펼치면 선다(`DETAIL_TABLE_PRIORITY.recipe`) */
        revealHidden
        columns={withDetailPriority('recipe', [
          ...recipePlanColumns<RecipePlanRow>({
            key: 'name',
            header: t('column.name'),
            cell: (r) => r.name,
          }),
          /*
           * [상세] — 작업 추가 창 레시피 표 줄 끝과 같은 단추 · 같은 팝업(eric 2026-10-22 「레시피에서... 상세 버튼 만들고.. 상세 버튼을
           * 누르면.. 기존의 레시피 내용이 나오게 해줘」). 🔴 조회만이라 막지 않는다 — 레시피 조회 · 요약은 viewer 도 받는다(`ROLES`)
           */
          {
            key: 'detail',
            header: '',
            align: 'end',
            nowrap: true,
            cell: (r) => (
              <Button
                aria-label={t('batchNew.recipe.detailOf', { name: r.name })}
                onClick={() => toggle(true)}
                size="pill"
              >
                {t('batchNew.recipe.detail')}
              </Button>
            ),
          },
        ])}
        emptyLabel={t('mask.none')}
        hideCount
        rowKey={() => 'recipe'}
        rows={[row]}
      />
      {open ? (
        <Suspense fallback={null}>
          <RecipeDetailPopup
            detail={detail}
            onClose={() => toggle(false)}
            title={`${name} · v${String(version)}`}
          />
        </Suspense>
      ) : null}
    </div>
  )
}

/**
 * 작업 상세 네 표의 처음 정렬(eric 2026-09-30 「모든 게시판」 머리글 정렬).
 * - 시료: **웰 오름차순 = 트레이 차례**(종전 `sampleRowsOf` 차례 그대로 · 머리글 ▲ 표시만 새로)
 * - 명령: 시각 최신 위(`/history` 가 `ts desc` 로 준다 — 같은 차례)
 * - 적재 · 수거: 열림 시각 최신 위
 * 🔴 펼침(`expandedKey` · `revealHidden`)은 행 키로 쥐어서 정렬을 바꿔도 펼친 행이 그대로 펼쳐져 있다.
 */
const SAMPLES_DEFAULT_SORT: TableSort = { key: 'wells', direction: 'asc' }
const COMMANDS_DEFAULT_SORT: TableSort = { key: 'ts', direction: 'desc' }
const TRAYS_DEFAULT_SORT: TableSort = { key: 'opened', direction: 'desc' }

/**
 * 시료 — 작업 추가 창의 «시료» 구역과 같은 꼴: **트레이 그림 + 칸표의 열**(eric 2026-10-22 「작업 선택했을 때 상세 내용이... 작업 추가의
 * 내용이 보이도록 해줘」). 그림과 시료 열은 추가 창과 같은 부품이다(`batchPlanParts.tsx` — `BatchTray` · `planSampleColumns`).
 *
 * - 열: **칸 · 웰 · 시료 번호 · 타입 · 단계 · 상태**. 추가 창 칸표(칸 · 웰 · 시료 번호 · 환자 번호 · 타입 · 단계 · [제거])에서
 *   🔴 **환자 번호는 뺐다**(eric 2026-09-30 「환자 번호 컬럼은 없애」 — 작업 상세 규칙) · [제거] 자리는 상태가 선다(만든 뒤에는 고를 것이 없다)
 * - 🔴 **행 하나가 시료 하나다**(종전 그대로 · 머리글 정렬 · 펼침 · 줄인 시료 번호도 그대로). 추가 창의 「비어 있음」 칸 줄은 세우지 않는다 —
 *   빈 칸은 그림이 보인다(칸 이름만 서고 시료 번호가 없다)
 *
 * 두 출처를 잇는다(`sampleRowsOf`):
 *   - **칸 · 웰 · 시료 번호** — `Batch.sample_assignments`(역할 무관) + 레시피 묶음(`sample_groups[].wells`)의 짝.
 *     칸은 시료 응답의 `sample_ref`, 못 봤으면 배정 웰이 든 묶음의 표지다(`SampleRow.sampleRef`)
 *   - **타입 · 단계 · 상태 · 펼침** — `GET /samples`(operator/admin 한정)
 *
 * 🔴 **`/samples` 를 못 봤으면(viewer 403) 시료 응답에서 오는 열을 «세우지 않는다».** 칸 · 웰 · 시료 번호(가림)만 서고 표 위에 사유가 선다.
 * 🔴 **보고서 열은 없다** — 보고서는 시료 목록 서랍 · 모니터링 서랍에서 연다.
 * ⚠ 웰은 **배정 축**이다(실행 내내 안 바뀐다). 「지금 어디 있나」는 펼침의 「현재 위치」가 말한다.
 */
function SamplesCard({
  assignments,
  historicalIds,
  historyIncomplete,
  historyNotice,
  samples,
  groups,
  view,
  loading,
  notice,
}: {
  assignments: Batch['sample_assignments']
  historicalIds?: readonly string[] | undefined
  historyIncomplete?: boolean | undefined
  historyNotice?: string | undefined
  samples: readonly Sample[]
  /** 레시피 묶음(`sampleGroupFacts(payload).groups`). 모르면 빈 배열 — 웰 칸이 서버 배정 웰 하나로 선다 */
  groups: readonly SampleGroupShape[]
  view: boolean
  loading: boolean
  /** 🔴 `/samples` 를 못 본 사유. 있으면 시료 응답에서 오는 열을 세우지 않는다 */
  notice?: string | undefined
}) {
  const [openKey, setOpenKey] = useState<string | null>(null)
  const known = notice === undefined
  const rows = sampleRowsOf({
    assignments,
    historicalIds,
    samples: known ? samples : undefined,
    groups,
    slots: TRAY_SLOTS,
  })
  const none = <span className="muted">{t('mask.none')}</span>

  /* 트레이 그림 — 레시피 묶음(표지가 있는 것만). 묶음을 모르면 서버 배정 웰만 «있음»으로 그린다(지어내지 않는다) */
  const trayGroups: TrayPlanGroup[] = groups.flatMap((g) =>
    g.sampleRef === undefined ? [] : [{ sample_ref: g.sampleRef, wells: g.wells }],
  )
  const assignedWells = new Set(assignments.map((a) => a.wellName))
  const captionOf = (sampleRef: string): string | undefined => {
    const row = rows.find((r) => r.sampleRef === sampleRef)
    return row === undefined ? undefined : shortSampleId(maskPii(row.sampleId, view))
  }

  /* 타입 · 단계 칸은 추가 창 칸표와 같은 칸이다(`planSampleColumns`) — 시료 응답이 없는 줄은 비운다 */
  const planCells = new Map(planSampleColumns(view).map((column) => [column.key, column.cell]))
  const planCell =
    (key: 'type' | 'stage') =>
    (row: SampleRow): ReactNode => {
      const cell = planCells.get(key)
      return row.sample === undefined || cell === undefined ? none : cell(row.sample)
    }

  const slotColumn: Column<SampleRow> = {
    key: 'slot',
    header: t('column.cell'),
    nowrap: true,
    sort: 'text',
    sortValue: (row) => row.sampleRef ?? null,
    cell: (row) =>
      row.sampleRef === undefined ? none : <span className="mono">{row.sampleRef}</span>,
  }
  const idColumn: Column<SampleRow> = {
    key: 'sampleId',
    header: t('column.sampleId'),
    nowrap: true,
    /*
     * 🔴 **가렸으면(`view` 없음) 정렬하지 않는다** — 가린 값의 차례로 원값의 차례가 샌다. 값 없음(`null`)이라
     *    머리글을 눌러도 받은 차례 그대로다
     */
    sort: 'text',
    sortValue: (row) => (view ? row.sampleId : null),
    // 🔴 시료 ID 는 PII 접촉면이다 — `view` 가 없으면 가린다
    cell: (row) => <span className="mono">{maskPii(row.sampleId, view)}</span>,
    /*
     * 🔴 넘치면 **끝자리를 남겨** 줄인다(`SAMPLE_ID_COMPACT`) — 넉넉하면 전체 그대로다.
     *    원값은 `title` · 보조기술 글자 · 행 펼침에 남는다. 가린 값(`view` 없음)은 줄이지 않는다 —
     *    `ShortId` 는 원값을 `title` 에 싣기 때문에 가린 자리에 쓰면 PII 가 샌다
     */
    compact: {
      priority: SAMPLE_ID_COMPACT.priority,
      cell: (row) =>
        view ? (
          <ShortId keep="tail" len={SAMPLE_ID_COMPACT.tail} value={row.sampleId} />
        ) : (
          <span className="mono">{maskPii(row.sampleId, view)}</span>
        ),
    },
  }
  const typeColumn: Column<SampleRow> = {
    key: 'type',
    header: t('column.sampleType'),
    sort: 'text',
    sortValue: (row) => row.sample?.sample_type ?? null,
    cell: planCell('type'),
  }
  const stageColumn: Column<SampleRow> = {
    key: 'stage',
    header: t('column.step'),
    nowrap: true,
    sort: 'text',
    sortValue: (row) =>
      row.sample === undefined ? null : enumLabel('enum.sampleStage', row.sample.stage),
    cell: planCell('stage'),
  }
  const statusColumn: Column<SampleRow> = {
    key: 'status',
    header: t('column.status'),
    nowrap: true,
    // 진행 중 → 완료 → 폐기(시료 목록과 같은 등급 · `sortRanks.ts`). 시료 응답에 없는 줄은 뒤
    sort: (a, b) => sampleStatusRank(a.sample?.status) - sampleStatusRank(b.sample?.status),
    cell: ({ sample: s }) =>
      s === undefined ? (
        none
      ) : (
        <StatusBadge flat label={sampleStatusLabel(s.status)} status={s.status} />
      ),
  }
  const wellColumn: Column<SampleRow> = {
    key: 'wells',
    header: t('column.well'),
    nowrap: true,
    /* 🔴 **첫 웰의 트레이 차례**(글자 순이면 `A10` 이 `A2` 앞) — 처음 차례(`sampleRowsOf`)와 같은 함수. 웰 없음은 맨 뒤 */
    sort: 'number',
    sortValue: (row) => trayWellRank(row.wells, TRAY_SLOTS),
    /* 🔴 배정 없는 시료는 비운다 — 「배정 없음」 줄을 따로 세우지 않는다 */
    cell: (row) =>
      row.wells.length === 0 ? none : <span className="mono">{row.wells.join(', ')}</span>,
  }
  /* 칸표 차례(칸 · 웰 · 시료 번호 · …). 좁으면 타입 → 단계 차례로 빠진다(`DETAIL_TABLE_PRIORITY.samples` · 값은 펼침에 선다) */
  const columns = withDetailPriority(
    'samples',
    known
      ? [slotColumn, wellColumn, idColumn, typeColumn, stageColumn, statusColumn]
      : [slotColumn, wellColumn, idColumn],
  )

  return (
    <div className="card">
      <div className="card__title">{t('card.batchDetail.samples')}</div>
      {/* 🔴 배정은 보이는데 시료 응답을 못 봤다 — 표 위에 사유. 배정도 없으면 표 안에 선다 */}
      {known || rows.length === 0 ? null : <StateNotice reason={notice} />}
      {historyNotice === undefined || rows.length === 0 || loading ? null : (
        <StateNotice reason={historyNotice} />
      )}
      {/* 추가 창의 「시료」 구역 꼴 — 그림 | 표. 서랍은 좁아서 위아래로 선다(`.nbtray--stack`) */}
      <div className="nbtray nbtray--stack">
        {historicalIds !== undefined && (loading || historyIncomplete) ? null : (
          <BatchTray
            captionOf={captionOf}
            cells={TRAY_SLOTS.map((slot) => ({ slot, present: assignedWells.has(slot) }))}
            groups={historicalIds === undefined ? trayGroups : []}
          />
        )}
        <div className="nbtray__list">
          <DataTable
            caption={t('card.batchDetail.samples')}
            columns={columns}
            defaultSort={SAMPLES_DEFAULT_SORT}
            emphasis={(row) => row.sample?.status === 'discarded'}
            /* 빠진 열 · 줄인 시료 번호의 원값이 펼침에 선다 */
            revealHidden
            /*
              🔴 「못 봤다 · 불러오는 중 · 0건」을 한 칸에 겹치지 않는다(2026-09-22) — 사유는 `notice`,
                 기다림은 `loading`(스켈레톤 행), 0건만 `emptyLabel` 이다.
            */
            emptyLabel={t('state.empty.samples')}
            loading={loading}
            {...(rows.length > 0 ? {} : { notice: notice ?? historyNotice })}
            {...(known
              ? {
                  expandedKey: openKey,
                  onToggleExpand: (key: string) =>
                    setOpenKey((current) => (current === key ? null : key)),
                  renderExpanded: ({ sample: s }: SampleRow) =>
                    s === undefined ? null : (
                      <div className="expand">
                        <ValueRows
                          rows={[
                            /* 단계는 열로 올라갔다(칸표와 같은 열) — 좁아서 빠지면 위 `revealHidden` 이 세운다 */
                            {
                              key: 'where',
                              label: t('column.currentLocation'),
                              value:
                                [s.current_module, s.current_position]
                                  .filter((v) => v != null && v !== '')
                                  .join(' · ') || t('mask.none'),
                            },
                            /*
                             * 검사 정보 — 시료 추가 창이 받는 칸 가운데 응답에 돌아오는 것만, 그 창의 이름 · 차례로(`sampleTestInfoRows`).
                             * 🔴 적혈구 농도 줄은 걷었다(eric 2026-10-23 「왜 적혈구 농도가 있어?」) — 시료 추가 창에 그 칸이 없다.
                             */
                            ...sampleTestInfoRows(s).map((row) => ({
                              ...row,
                              value: row.value === '' ? t('mask.none') : row.value,
                            })),
                            ...(s.discard_reason == null
                              ? []
                              : [
                                  {
                                    key: 'discard',
                                    label: t('column.reasonChange'),
                                    value: s.discard_reason,
                                  },
                                ]),
                          ]}
                        />
                      </div>
                    ),
                }
              : {})}
            rowKey={(row) => row.sampleId}
            rows={rows}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * 명령 — 🔴 **감사 원장이다**(`GET /history?event_type=audit`). 2026-09-20 에 축을 바꿨다.
 *
 * 종전에는 `event_type=command` 를 쓰려 했는데 **그 원장으로는 배치를 고를 수 없다**:
 *   - `target` 에 접두어가 없다(모듈 id 원본) — `batch:` 로 거를 수 없고
 *   - `batch_id` 열은 표에 있지만 `/history` 응답에 실리지 않으며
 *   - `log_level`(= 종전 「종류」 열)은 **아무도 채우지 않는다**(`app/services/command_log.py`)
 * 배치 명령(`createBatch`·`startBatch`·`pauseBatch`·`recoverBatch` …)은 전부 감사 원장에
 * `target = "batch:<id>"` 로 남는다(`app/views/batches.py` `_write_audit`). 그쪽이 근거다.
 *
 * 🔴 **「작업자」 열은 admin 에게만 선다**(eric 2026-09-30 「행위자를 유저 이름으로 쓰고.. 권한이 없는 사람이면..
 *    행위자를 빼.. 행위자 컬럼명을 작업자라고 해줘」). `actor` 는 **계정 ID**(`user_id` — UUID 이거나 `dev-runner` 같은
 *    글자)이고 이름은 admin 전용 `GET /users` 로만 풀린다. 그래서 작업 목록 · 「작업」 카드의 작업자와 **같은 이름표**
 *    (`owners` · `useBatchOwners`)를 받았을 때만 열을 세운다 — operator · viewer 에게는 열 자체가 없다(id 도 안 샌다).
 *    표에 없는 id 는 개별 조회 · 그래도 없으면(`system` 등) 원문 그대로다(`ownerValue`).
 * 🔴 **「명령 ID」 열은 없다**(같은 날 「명령 ID 컬럼도 빼줘」) — 행을 펼쳐도 안 선다. 값은 행 키에만 쓴다.
 */
function CommandsCard({
  rows,
  loading,
  notice,
  owners,
}: {
  rows: readonly HistoryEntry[]
  loading: boolean
  notice?: string | undefined
  /** 작업자 이름표 — 넘겼을 때만(admin) 「작업자」 열이 선다 */
  owners?: BatchOwners | undefined
}) {
  const [openKey, setOpenKey] = useState<string | null>(null)
  const columns: Column<HistoryEntry>[] = [
    {
      key: 'ts',
      header: t('column.time'),
      nowrap: true,
      sort: 'time',
      sortValue: (row) => row.ts,
      cell: (row) => <span className="muted mono num">{timeSecText(row.ts)}</span>,
    },
    {
      key: 'operation',
      header: t('column.operation'),
      nowrap: true,
      sort: 'text',
      sortValue: (row) => operationText(detailText(row, 'operation')),
      cell: (row) => operationText(detailText(row, 'operation')),
    },
    {
      key: 'reason',
      /*
       * 🔴 **사람이 적은 사유만 그린다**(2026-09-20 실물에서 고쳤다). 서버는 사유가 없는
       *    명령에 `operation=… target=… actor=… command_id=… changed=none` 을 «합성»해
       *    넣는데(`IFC-COM-15 ⓑ`), 그것을 그대로 뿌리니 **UUID 두 개가 가로로 뻗어 뒤 열을
       *    밀어냈다.** 그 안의 값은 동작 · 작업자 열이 이미 말하거나 화면이 안 보이기로 한 것(명령 ID)이다 — 판정은 `humanReason()`.
       */
      header: t('column.reason'),
      sort: 'text',
      sortValue: (row) => humanReason(row),
      cell: (row) => <span className="muted">{orDash(humanReason(row))}</span>,
    },
    ...(owners === undefined
      ? []
      : [
          {
            key: 'actor',
            header: t('column.owner'),
            nowrap: true,
            // 🔴 칸이 그리는 **이름**으로 세운다(작업 목록 「작업자」와 같다). 못 푼 id 는 값 없음 — 맨 뒤
            sort: 'text' as const,
            sortValue: (row: HistoryEntry) =>
              row.actor == null ? null : owners.byId.get(row.actor)?.name,
            cell: (row: HistoryEntry) => ownerValue(owners, row.actor ?? null),
          },
        ]),
  ]

  return (
    <div className="card">
      <div className="card__title">{t('card.batchDetail.cmds')}</div>
      <DataTable
        caption={t('card.batchDetail.cmds')}
        /* 🔴 넘치면 사유 → 작업자 순으로 빠지고 행을 펼치면 선다(`DETAIL_TABLE_PRIORITY`) */
        columns={withDetailPriority('commands', columns)}
        defaultSort={COMMANDS_DEFAULT_SORT}
        emptyLabel={t('state.empty.events')}
        expandedKey={openKey}
        onToggleExpand={(key: string) => setOpenKey((current) => (current === key ? null : key))}
        revealHidden
        loading={loading}
        {...(notice === undefined ? {} : { notice })}
        rowKey={(row) => `${row.ts}-${commandIdOf(row) ?? ''}`}
        rows={rows}
      />
    </div>
  )
}

/**
 * 적재·수거 — 🔴 **eric 목록(작업 · 시료 · 명령)에 없는데 남긴 카드다**(2026-09-30 · 팀장 판단).
 *    콘솔에서 [적재 확약]을 하는 창구가 **여기 하나뿐이다**(`useConfirmLoad` 호출부가 `BatchDetailContainer`
 *    하나). 빼면 실제 운전이 막힌다. 확약 대기가 있으면 제목 옆 배지가 눈에 띄고, 없으면 조용히 선다.
 *    종전 요약의 건수 줄(입고 적재 · 출구 수거 · 확약 대기)은 이 배지와 두 표로 접었다.
 *
 * 적재·수거는 **작업과 독립된 원장**이다. 배치가 끝나도 수거 건이 남을 수 있다(계약 `TrayUnload`).
 *
 * 🔴 **수거 확약은 다음 배치의 선결이다** — 안 하면 염색기가 `IDLE` 로 안 돌아온다.
 * 🔴 확약 버튼의 활성 근거는 **`confirmed_at === null`** 이다. `allowed_commands` 가 아니다.
 * ⚠ **모듈이 409 로 막을 수 있다**(평형추를 요구하는 회차 등). 그때는 그 사유를 **그 자리에**
 *   보인다 — 화면이 미리 막거나 문구를 지어내지 않는다.
 */
function TraysCard({
  loadDefaultsEnabled,
  loadModuleType,
  localizeTrayPoints,
  load,
  unload,
  access,
  loading,
  confirming,
  confirmError,
  confirmUnavailableReason,
  onConfirmLoad,
  onConfirmUnload,
}: {
  loadDefaultsEnabled: boolean
  loadModuleType: ((load: TrayLoad) => string | null | undefined) | undefined
  localizeTrayPoints: boolean
  load: readonly TrayLoad[]
  unload: readonly TrayUnload[]
  access: ScreenAccess
  loading: boolean
  confirming: boolean
  confirmError?: string | undefined
  confirmUnavailableReason?: string | undefined
  onConfirmLoad?:
    ((loadId: string, report: LoadConfirmReport) => void | Promise<unknown>) | undefined
  onConfirmUnload?: ((unloadId: string) => void) | undefined
}) {
  const ConfirmModal = loadDefaultsEnabled ? LoadConfirmWithDefault : LoadConfirmModal
  /** 적재 확약 창이 열린 건. 🔴 id 만 쥔다 — 게이트는 **지금 표에 있는 행**으로 다시 본다 */
  const [loadPromptId, setLoadPromptId] = useState<string | null>(null)
  const loadPrompt =
    loadPromptId === null ? undefined : load.find((row) => row.load_id === loadPromptId)
  /** 펼친 행(빠진 열을 보려고). 적재 · 수거 표가 같이 쥔다 — 키가 `load_id` · `unload_id` 라 겹치지 않는다 */
  const [openKey, setOpenKey] = useState<string | null>(null)
  const toggle = (key: string) => setOpenKey((current) => (current === key ? null : key))

  /** 🔴 **제어락을 요구하지 않는다** — 실물이 역할만 본다(`require_roles({operator, admin})`) */
  const confirmGate = (row: { confirmed_at?: string | null }, handler: unknown) =>
    confirming
      ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
      : handler === undefined
        ? ({
            disabled: true,
            disabledReason: confirmUnavailableReason ?? t('reason.demoSource'),
          } as const)
        : gate(access, {
            perm: 'control',
            stateOk: row.confirmed_at == null,
            stateReason: 'reason.alreadyConfirmed',
          })

  const pointText = (row: TrayLoad | TrayUnload) => {
    const point =
      localizeTrayPoints &&
      [
        'centrifuge.grip_tray_on_slide',
        'assembler.grip_tray_on_slide',
        'staining.grip_tray_on_slide',
      ].includes(row.point_id)
        ? t('monitoring.robot.point.slide_rear')
        : row.point_id
    return `${historyTargetText(row.module_id)} · ${point}`
  }

  const shared = <T extends TrayLoad | TrayUnload>(): Column<T>[] => [
    {
      key: 'point',
      header: t('panel.handoffPoint'),
      nowrap: true,
      sort: 'text',
      sortValue: pointText,
      cell: (row) =>
        localizeTrayPoints ? <span title={row.point_id}>{pointText(row)}</span> : pointText(row),
    },
    {
      key: 'opened',
      header: t('panel.openedAt'),
      nowrap: true,
      sort: 'time',
      sortValue: (row) => row.opened_at,
      cell: (row) => <span className="muted mono num">{dateTimeText(row.opened_at)}</span>,
    },
    {
      key: 'confirmed',
      header: t('panel.confirmedAt'),
      nowrap: true,
      /*
       * 🔴 `null` = 확약 대기다. 빈칸으로 두면 「모른다」와 구별되지 않는다.
       * 정렬에서는 값 없음이라 방향 무관 맨 뒤다 — 대기 건은 제목 옆 배지가 먼저 말한다
       */
      sort: 'time',
      sortValue: (row) => row.confirmed_at,
      cell: (row) =>
        row.confirmed_at == null ? (
          <Badge variant="inconclusive">{t('value.awaitingConfirm')}</Badge>
        ) : (
          <span className="muted mono num">{dateTimeText(row.confirmed_at)}</span>
        ),
    },
    {
      key: 'by',
      header: t('column.actor'),
      nowrap: true,
      /* ⚠ 무인증 로컬 확약이면 `confirmed_by` 가 null 이고 귀속 근거는 `attribution_basis` 가 쥔다 */
      sort: 'text',
      sortValue: (row) => row.confirmed_by,
      cell: (row) => <span className="muted mono">{orDash(row.confirmed_by)}</span>,
    },
    {
      key: 'source',
      header: t('column.source'),
      nowrap: true,
      /* 확약 **창구**(surface) 축이다 — 「누가」가 아니라 「어디서」 */
      sort: 'text',
      sortValue: (row) =>
        row.confirm_source == null ? null : enumLabel('enum.confirmSource', row.confirm_source),
      cell: (row) => (
        <span className="muted">
          {row.confirm_source == null
            ? t('mask.none')
            : enumLabel('enum.confirmSource', row.confirm_source)}
        </span>
      ),
    },
  ]

  const loadColumns: Column<TrayLoad>[] = [
    ...shared<TrayLoad>(),
    {
      key: 'chambers',
      header: t('panel.loadedChambers'),
      nowrap: true,
      /* ⚠ **계측이 아니라 신고다**. L2 경유 확약은 이 값을 채우지 못한다(BR-06) */
      sort: 'number',
      sortValue: (row) => row.loaded_chambers,
      cell: (row) => (
        <span className="mono num">
          {row.loaded_chambers == null ? t('mask.none') : String(row.loaded_chambers)}
        </span>
      ),
    },
    {
      key: 'act',
      header: '',
      align: 'end',
      nowrap: true,
      cell: (row) => (
        <Button
          onClick={() => setLoadPromptId(row.load_id)}
          size="pill"
          {...confirmGate(row, onConfirmLoad)}
        >
          {t('action.confirmLoad')}
        </Button>
      ),
    },
  ]

  const unloadColumns: Column<TrayUnload>[] = [
    ...shared<TrayUnload>(),
    {
      key: 'act',
      header: '',
      align: 'end',
      nowrap: true,
      cell: (row) => (
        <Button
          onClick={() => onConfirmUnload?.(row.unload_id)}
          size="pill"
          {...confirmGate(row, onConfirmUnload)}
        >
          {t('action.confirmUnload')}
        </Button>
      ),
    },
  ]

  /*
   * 🔴 **빈 문구를 표마다 가른다**(2026-09-20 실물에서 고쳤다). 둘 다 「적재 · 수거 기록
   *    없음」이었는데, 적재는 1건 있고 수거만 없는 회차에서 **아래 표가 「둘 다 없다」고
   *    말했다.** 없는 것은 수거뿐이다.
   */
  const emptyOf = (key: MessageKey) => (loading ? t('state.loading') : t(key))
  /* 🔴 판정 축은 `confirmed_at === null` 하나다 */
  const pendingCount = pendingTrays(load).length + pendingTrays(unload).length

  return (
    <div className="card">
      <div className="card__title btnrow">
        {t('card.batchDetail.trays')}
        {pendingCount === 0 ? null : (
          <Badge variant="inconclusive">{`${t('row.pendingConfirm')} ${String(pendingCount)}`}</Badge>
        )}
      </div>
      {/* 🔴 확약 실패는 **표 위 그 자리에** 남는다. 모듈이 준 409 문면이 여기 뜬다 */}
      {/* ⚠ 적재 확약 창이 떠 있으면 그 문면은 **창 안에** 선다 — 같은 알림을 두 번 읽히지 않는다 */}
      {confirmError === undefined || loadPrompt !== undefined ? null : (
        <p className="deny" role="alert">
          {confirmError}
        </p>
      )}
      {/*
        🔴 **제목이 눈에 보여야 한다**(2026-09-20). `DataTable` 의 `caption` 은 `a-sr` 이라
           화면에 안 나온다 — 탭 하나에 표가 **둘**인 자리는 이 탭뿐이라 다른 탭에서는
           탭 이름이 제목 노릇을 하지만, 여기서는 **제목 없는 표 두 개가 연달아 붙어**
           열 이름까지 거의 같았다. 보는 사람이 어느 쪽이 수거인지 알 길이 없었다.
      */}
      <div className="sectitle">{t('panel.trayLoad')}</div>
      <DataTable
        caption={t('panel.trayLoad')}
        columns={withDetailPriority('trays', loadColumns)}
        defaultSort={TRAYS_DEFAULT_SORT}
        density="compact"
        emptyLabel={emptyOf('state.empty.trayLoad')}
        expandedKey={openKey}
        onToggleExpand={toggle}
        revealHidden
        rowKey={(row) => row.load_id}
        rows={load}
      />
      <div className="sectitle">{t('panel.trayUnload')}</div>
      <DataTable
        caption={t('panel.trayUnload')}
        columns={withDetailPriority('trays', unloadColumns)}
        defaultSort={TRAYS_DEFAULT_SORT}
        density="compact"
        emptyLabel={emptyOf('state.empty.trayUnload')}
        expandedKey={openKey}
        onToggleExpand={toggle}
        revealHidden
        rowKey={(row) => row.unload_id}
        rows={unload}
      />
      {loadPrompt === undefined ? null : (
        <ConfirmModal
          key={loadPrompt.load_id}
          error={confirmError}
          gate={confirmGate(loadPrompt, onConfirmLoad)}
          load={loadPrompt}
          // live 창(`LoadConfirmWithDefault`)은 서버가 준 종류로 이 값을 덮는다
          moduleType={loadModuleType?.(loadPrompt)}
          onClose={() => setLoadPromptId(null)}
          onSubmit={(report) => {
            const sent = onConfirmLoad?.(loadPrompt.load_id, report)
            void Promise.resolve(sent).then(
              () => setLoadPromptId(null),
              () => undefined,
            )
          }}
        />
      )}
    </div>
  )
}

/**
 * 🔴 **사유를 받아야 하는 명령 둘**(복구 판정의 폐기 · 완료 확정). 계약이 `reason` 을
 *    필수로 세운 자리다 — 프론트가 채우면 감사 원장이 거짓이 된다.
 * ⚠ `recover` 의 `resume` 은 여기 없다 — 그 갈래만 사유 없이 성립한다.
 * ⚠ 중지(`stop`)도 없다 — 머리 단추 줄을 걷었다(eric 2026-09-30). 중지의 사유 대화는 작업 줄(`StripCommandDialog`)이 쥔다.
 */
type ReasonPrompt =
  | { kind: 'recover'; decision: 'discard' | 'confirm_completed' }
  /** 대기 작업 취소(`stop` · 시작 전 작업만 · eric 2026-10-29) */
  | { kind: 'cancel' }

/** 🔴 키가 **전수 대응**이라 폴백이 없다 — 없는 키를 기본값으로 두면 조용히 틀린 문구가 뜬다 */
type PromptKey = 'discard' | 'confirm_completed' | 'cancel'

const promptKey = (prompt: ReasonPrompt): PromptKey =>
  prompt.kind === 'cancel' ? 'cancel' : prompt.decision

const PROMPT_TITLE: Record<PromptKey, MessageKey> = {
  discard: 'screen.batchDiscard.title',
  confirm_completed: 'screen.batchConfirmCompleted.title',
  cancel: 'batch.cancel.title',
}

const PROMPT_ACTION: Record<PromptKey, MessageKey> = {
  discard: 'action.discard',
  confirm_completed: 'action.confirmCompleted',
  cancel: 'batch.cancel.action',
}

/**
 * 되돌릴 수 없는 명령의 확인 대화.
 *
 * 🔴 **실패해도 닫히지 않는다** — 적은 사유를 다시 적게 하지 않으려는 것이다.
 *    성공 판정은 하지 않는다: 무효화 → 재조회로 `allowed_commands` 가 바뀌고 그때 버튼이
 *    비활성된다. 닫는 것은 사람이 한다(취소) — 「보냈으니 닫는다」로 하면 거부를 못 본다.
 */
function ReasonModal({
  prompt,
  batchId,
  reason,
  onReason,
  onSubmit,
  onClose,
  error,
  disabled,
  disabledReason,
}: {
  prompt: ReasonPrompt
  batchId: string
  reason: string
  onReason: (next: string) => void
  onSubmit: () => void
  onClose: () => void
  error: string | undefined
} & ActionGate) {
  const key: PromptKey = promptKey(prompt)
  const validation = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')
  const gateProps: ActionGate = disabled ? { disabled: true, disabledReason } : { disabled: false }

  return (
    <Modal
      size="sm"
      footer={
        <>
          <Button onClick={onClose} size="pill">
            {t('action.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (gateProps.disabled) return
              if (
                !validation.validate(
                  reason,
                  document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
                )
              )
                return
              onSubmit()
            }}
            size="pill"
            variant="danger"
            {...gateProps}
          >
            {t(PROMPT_ACTION[key])}
          </Button>
        </>
      }
      onClose={onClose}
      title={t(PROMPT_TITLE[key])}
    >
      {/*
        🔴 설명 문장을 달지 않는다(§1-1). 대상과 성질을 값으로만 보인다.
        ⚠ 예외 하나 — 대기 작업 취소는 «시료가 풀린다»는 부수효과를 말해야 해서 한 문장을 둔다(팀장 결정 2026-10-29).
      */}
      {key === 'cancel' ? <p className="batch-cancel__body">{t('batch.cancel.body')}</p> : null}
      <ValueRows
        rows={[
          { key: 'batch', label: t('column.batch'), value: batchId },
          { key: 'reversible', label: t('row.reversible'), value: t('value.irreversible') },
        ]}
      />
      {/* 🔴 계약 필수값. 적은 문장이 **그대로** 감사행에 실린다 — 합성하지 않는다 */}
      <Field {...reasonField.fieldProps} label={t('field.reason')}>
        <TextInput
          {...reasonField.inputProps}
          onChange={(next) => {
            onReason(next)
            validation.change('reason', next)
          }}
          value={reason}
          wide
        />
      </Field>
      {error === undefined ? null : (
        <p className="vissue" role="alert">
          {error}
        </p>
      )}
    </Modal>
  )
}
